<template>
  <v-container fluid class="tcls">
    <page-skeleton v-if="loading" variant="cards" class="mt-4" />

    <template v-else>
      <!-- ===== Cabeçalho ===== -->
      <header class="tcls-head">
        <div>
          <div class="tcls-eyebrow">{{ $t("agenda.teacher") }}</div>
          <h1 class="tcls-title tcls-display">
            {{ $t("partnerClasses.title") }}
          </h1>
          <p class="tcls-sub">
            {{ $t("partnerClasses.sub") }}
          </p>
        </div>

        <div class="tcls-tallies">
          <div class="tcls-tally">
            <span class="tcls-tally__num tcls-display">{{
              classes.length
            }}</span>
            <span class="tcls-tally__label">{{
              $t("partnerClasses.total")
            }}</span>
          </div>
          <div class="tcls-tally">
            <span
              class="tcls-tally__num tcls-display tcls-tally__num--active"
              >{{ activeClasses }}</span
            >
            <span class="tcls-tally__label">{{
              $t("partnerClasses.active")
            }}</span>
          </div>
          <div class="tcls-tally">
            <span class="tcls-tally__num tcls-display">{{
              pendingClasses
            }}</span>
            <span class="tcls-tally__label">{{
              $t("partnerClasses.forming")
            }}</span>
          </div>
          <div v-if="requestsTotal" class="tcls-tally">
            <span class="tcls-tally__num tcls-display tcls-tally__num--ask">{{
              requestsTotal
            }}</span>
            <span class="tcls-tally__label">{{
              $tc("partnerClasses.requests", requestsTotal)
            }}</span>
          </div>
        </div>
      </header>

      <!-- ===== Busca + filtro ===== -->
      <div v-if="classes.length" class="tcls-toolbar">
        <v-text-field
          v-model="search"
          dense
          outlined
          hide-details
          clearable
          :placeholder="$t('partnerClasses.searchCode')"
          prepend-inner-icon="mdi-magnify"
          class="tcls-search"
        />

        <div class="tcls-segments" role="tablist">
          <button
            v-for="seg in statusSegments"
            :key="seg.value"
            type="button"
            class="tcls-segment"
            :class="{ 'tcls-segment--on': statusFilter === seg.value }"
            @click="statusFilter = seg.value"
          >
            {{ seg.text }}
          </button>
        </div>
      </div>

      <!-- ===== Lista ===== -->
      <div v-if="filteredClasses.length" class="tcls-grid">
        <article
          v-for="(item, index) in filteredClasses"
          :key="item.id || index"
          class="tcls-card"
          :style="{ '--i': index }"
        >
          <div class="tcls-card__top">
            <div class="tcls-card__eyebrow">
              {{ $t("partnerClasses.eyebrow", { code: item.cod || "—" }) }}
            </div>

            <!-- Aluno pedindo para entrar: o professor precisa abrir a turma
                 para aceitar ou recusar, e nada na lista avisava disso. -->
            <span
              v-if="pendingRequests(item).length"
              class="tcls-ask"
              :title="requestsLabel(item)"
            >
              <v-icon size="14" color="#fff">mdi-account-clock-outline</v-icon>
              {{ requestsLabel(item) }}
            </span>

            <span
              class="tcls-status"
              :class="`tcls-status--${statusMeta(item.status).tone}`"
            >
              <span class="tcls-status__dot"></span
              >{{ statusMeta(item.status).label }}
            </span>
          </div>

          <!-- Com o nivel fora, a turma se identifica pelo horario: ele assume
               o titulo do card. Cada encontro numa linha, com o dia colado no
               seu proprio horario -- weekDays e hours vem pareados por indice,
               e resumi-los numa frase perdia essa relacao. -->
          <div class="tcls-when">
            <ul v-if="classTimes(item).length" class="tcls-slots">
              <li
                v-for="(slot, si) in classTimes(item)"
                :key="`s-${si}`"
                class="tcls-slot"
              >
                <span class="tcls-slot__day">{{
                  weekDayShortLabel(slot.weekDay)
                }}</span>
                <span class="tcls-slot__time tcls-display">
                  <time>{{ slot.start }}</time>
                  <span class="tcls-slot__dash" aria-hidden="true">–</span>
                  <time>{{ slot.end }}</time>
                </span>
              </li>
            </ul>
            <p v-else class="tcls-when__empty tcls-display">
              {{ $t("partnerClasses.noSchedule") }}
            </p>
          </div>

          <div class="tcls-card__foot">
            <div class="tcls-people__count">
              {{
                $tc("partnerClasses.students", activeStudents(item).length, {
                  count: activeStudents(item).length,
                })
              }}
            </div>

            <!-- Um aluno por linha: a foto identifica, o nome ao lado confirma.
                 A turma vai ate quinteto, entao cabe inteira. -->
            <ul v-if="activeStudents(item).length" class="tcls-roster">
              <li
                v-for="(student, si) in activeStudents(item)"
                :key="student.id || si"
                class="tcls-member"
              >
                <span class="tcls-avatar">
                  <img
                    v-if="student.avatar"
                    :src="student.avatar"
                    :alt="$t('partnerClasses.photoOf', { name: student.name })"
                    loading="lazy"
                  />
                  <span v-else aria-hidden="true">{{
                    studentInitials(student.name)
                  }}</span>
                </span>
                <span class="tcls-member__name" :title="student.name">{{
                  student.name
                }}</span>
              </li>
            </ul>

            <p v-else class="tcls-roster__empty">
              {{ $t("partnerClasses.noActive") }}
            </p>

            <div class="tcls-card__meta">
              {{ formatLabel(item.format) }} ·
              {{ frequencyLabel(item.frequency) }}
            </div>
          </div>

          <button
            type="button"
            class="tcls-manage"
            @click="classDetails(item.id)"
          >
            {{
              pendingRequests(item).length
                ? $t("partnerClasses.review")
                : $t("partnerClasses.manage")
            }}
            <v-icon size="18" color="#fff">mdi-arrow-right</v-icon>
          </button>
        </article>
      </div>

      <!-- ===== Vazio: filtro sem resultado ===== -->
      <div v-else-if="classes.length" class="tcls-empty">
        <v-icon size="34" color="#A826E1">mdi-filter-remove-outline</v-icon>
        <div class="tcls-empty__title">
          {{ $t("partnerClasses.emptyFilterTitle") }}
        </div>
        <div class="tcls-empty__text">
          {{ $t("partnerClasses.emptyFilterText") }}
        </div>
        <button type="button" class="tcls-empty__btn" @click="resetFilters">
          {{ $t("course.clearFilters") }}
        </button>
      </div>

      <!-- ===== Vazio: nenhuma turma ===== -->
      <div v-else class="tcls-empty">
        <v-icon size="34" color="#A826E1"
          >mdi-book-open-page-variant-outline</v-icon
        >
        <div class="tcls-empty__title">
          {{ $t("partnerClasses.emptyTitle") }}
        </div>
        <div class="tcls-empty__text">
          {{ $t("partnerClasses.emptyText") }}
        </div>
      </div>
    </template>
  </v-container>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";
import {
  displayGroupFormat,
  displayGroupFrequency,
  initialsFromName,
  normalizeClassTimes,
  weekDayShortLabel,
} from "@/shared/classPresentation";

const STATUS_META = {
  0: { label: "classHeader.forming", tone: "waiting" },
  1: { label: "classHeader.active", tone: "active" },
  2: { label: "classHeader.closed", tone: "off" },
};

export default {
  name: "PartnerClasses",

  data() {
    return {
      loading: true,
      classes: [],
      search: "",
      statusFilter: "all",
    };
  },

  created() {
    partnerApi
      .getStudyGroups()
      .then((res) => {
        this.classes = Array.isArray(res.data) ? res.data : [];
      })
      .catch(() => {
        this.$root.$emit("display-alert", {
          text: this.$t("partnerClasses.loadFailed"),
          type: 1,
        });
      })
      .finally(() => {
        this.loading = false;
      });
  },

  computed: {
    statusSegments() {
      return [
        { text: this.$t("partnerClasses.all"), value: "all" },
        { text: this.$t("partnerClasses.activeSeg"), value: 1 },
        { text: this.$t("partnerClasses.formingSeg"), value: 0 },
        { text: this.$t("partnerClasses.closedSeg"), value: 2 },
      ];
    },
    activeClasses() {
      return this.classes.filter((item) => +item.status === 1).length;
    },
    pendingClasses() {
      return this.classes.filter((item) => +item.status === 0).length;
    },
    requestsTotal() {
      return this.classes.reduce(
        (total, item) => total + this.pendingRequests(item).length,
        0
      );
    },
    filteredClasses() {
      const term = (this.search || "").trim().toLowerCase();

      return this.classes.filter((item) => {
        if (this.statusFilter !== "all" && +item.status !== this.statusFilter) {
          return false;
        }

        if (
          term &&
          !String(item.cod || "")
            .toLowerCase()
            .includes(term)
        ) {
          return false;
        }

        return true;
      });
    },
  },

  methods: {
    formatLabel(format) {
      return displayGroupFormat(format);
    },
    frequencyLabel(frequency) {
      return displayGroupFrequency(frequency);
    },
    statusMeta(status) {
      const meta = STATUS_META[+status];
      if (!meta) {
        return {
          label: this.$t("enums.participantStatus.none"),
          tone: "off",
        };
      }
      return { label: this.$t(meta.label), tone: meta.tone };
    },
    // weekDays e hours chegam como arrays paralelos pareados por indice
    // (weekDays[i] acontece em hours[i]); o helper faz esse casamento.
    classTimes(item) {
      return normalizeClassTimes(item.weekDays, item.hours);
    },
    weekDayShortLabel,
    activeStudents(item) {
      return Array.isArray(item.content)
        ? item.content.filter((student) => +student.status === 1)
        : [];
    },
    // Alunos com pedido aguardando resposta (status 0 no vínculo).
    pendingRequests(item) {
      return Array.isArray(item.content)
        ? item.content.filter((student) => +student.status === 0)
        : [];
    },
    requestsLabel(item) {
      const total = this.pendingRequests(item).length;
      return this.$tc("partnerClasses.requestsCount", total, { count: total });
    },
    studentInitials(name) {
      return initialsFromName(name) || "AL";
    },
    resetFilters() {
      this.search = "";
      this.statusFilter = "all";
    },
    classDetails(classId) {
      this.$router.push(`/connect/professor/turmas/${classId}`);
    },
  },
};
</script>

<style scoped>
.tcls {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --paper: var(--dk-paper, #faf7fc);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --grape: var(--dk-grape, #a826e1);
  --grape-soft: rgba(168, 38, 225, 0.1);
  --teal: var(--dk-teal, #00a379);
  --ember: var(--dk-ember, #ff5d2a);
  padding: 8px 4px 40px;
  color: var(--ink);
}

.tcls-display {
  font-family: "Poppins", sans-serif;
  letter-spacing: -0.01em;
}

/* ---------- Cabeçalho ---------- */
.tcls-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-top: 12px;
  flex-wrap: wrap;
}
.tcls-eyebrow {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--grape);
}
.tcls-title {
  margin: 4px 0 0;
  font-size: clamp(1.9rem, 4vw, 2.6rem);
  font-weight: 700;
  line-height: 1.04;
  color: var(--ink);
}
.tcls-sub {
  margin: 8px 0 0;
  max-width: 48ch;
  font-size: 0.9rem;
  color: var(--muted);
  line-height: 1.5;
}
.tcls-tallies {
  display: flex;
  gap: 26px;
  padding-top: 4px;
}
.tcls-tally {
  display: flex;
  flex-direction: column;
}
.tcls-tally__num {
  font-size: 1.9rem;
  font-weight: 700;
  line-height: 1;
  color: var(--ink);
}
.tcls-tally__num--active {
  color: var(--grape);
}
.tcls-tally__label {
  margin-top: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
}

/* ---------- Toolbar ---------- */
.tcls-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 26px;
  flex-wrap: wrap;
}
.tcls-search {
  max-width: 280px;
  flex: 1 1 220px;
}
.tcls-search >>> .v-input__slot {
  border-radius: 14px;
  background: var(--dk-surface, #fff);
}
.tcls-segments {
  display: inline-flex;
  padding: 4px;
  border-radius: 14px;
  background: var(--paper);
  border: 1px solid var(--line);
  flex-wrap: wrap;
}
.tcls-segment {
  padding: 7px 14px;
  border-radius: 10px;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--muted);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.tcls-segment:hover {
  color: var(--ink);
}
.tcls-segment--on {
  background: var(--dk-surface, #fff);
  color: var(--grape);
  box-shadow: 0 6px 16px -10px rgba(168, 38, 225, 0.7);
}
.tcls-segment:focus-visible {
  outline: 2px solid var(--grape);
  outline-offset: 2px;
}

/* ---------- Grade de cards ---------- */
.tcls-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
  gap: 18px;
  margin-top: 22px;
}
.tcls-card {
  display: flex;
  flex-direction: column;
  padding: 22px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 20px;
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  animation: tcls-rise 0.4s ease both;
  animation-delay: calc(var(--i, 0) * 45ms);
}
.tcls-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 26px 46px -34px rgba(168, 38, 225, 0.5);
}
@keyframes tcls-rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
}

.tcls-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.tcls-card__eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--grape);
}
.tcls-status {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.tcls-status__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.tcls-ask {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--ember);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.tcls-tally__num--ask {
  color: var(--ember);
}
.tcls-status--active {
  background: rgba(0, 163, 121, 0.12);
  color: var(--dk-teal-deep, #007f5e);
}
.tcls-status--waiting {
  background: rgba(255, 93, 42, 0.12);
  color: var(--dk-ember-deep, #b5421e);
}
.tcls-status--off {
  background: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.07);
  color: var(--muted);
}

/* ---------- horários: o título do card ---------- */
/* O dia e uma etiqueta categorica de largura fixa e o horario um intervalo
   numerico. Em colunas separadas, varios encontros se comparam de cima para
   baixo -- leitura que uma frase com separadores nao permite. */
.tcls-when {
  margin-top: 14px;
}
.tcls-slots {
  list-style: none;
  margin: 0;
  padding: 0;
}
.tcls-slot {
  display: flex;
  align-items: center;
  gap: 12px;
}
.tcls-slot + .tcls-slot {
  margin-top: 6px;
}
.tcls-slot__day {
  flex: 0 0 auto;
  min-width: 46px;
  padding: 5px 0;
  border-radius: 9px;
  background: var(--grape-soft);
  color: var(--grape);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: center;
}
/* Herda o slot que era do nivel: mesmo tamanho e mesma face de display, para o
   card manter a silhueta tipografica. */
.tcls-slot__time {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-size: 1.45rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.tcls-slot__dash {
  font-weight: 400;
  color: var(--grape);
}
.tcls-when__empty {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--muted);
}

/* ---------- rodapé: quem está na turma ---------- */
.tcls-card__foot {
  margin-top: 16px;
  margin-bottom: 18px;
  padding-top: 14px;
  border-top: 1px solid var(--line);
}
.tcls-people__count {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
}
.tcls-roster {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
}
.tcls-member {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 7px 0;
  border-top: 1px solid var(--line);
}
.tcls-member:first-child {
  border-top: none;
  padding-top: 2px;
}
.tcls-member__name {
  min-width: 0;
  flex: 1;
  font-size: 0.92rem;
  font-weight: 700;
  line-height: 1.3;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* 36px para a foto ler como rosto, e sem sobreposicao: em coluna cada aluno
   tem a sua linha. */
.tcls-avatar {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    160deg,
    var(--dk-grape, #c56eeb),
    var(--dk-grape, #a826e1)
  );
  color: #fff;
  font-size: 0.72rem;
  font-weight: 800;
  border: 2px solid var(--dk-surface, #fff);
  box-shadow: 0 0 0 1px var(--grape-soft);
}
.tcls-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.tcls-roster__empty {
  margin: 8px 0 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--muted);
}
.tcls-card__meta {
  margin-top: 12px;
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--muted);
}

.tcls-manage {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  /* A lista de alunos varia de tamanho; o botao encosta no rodape para os
     cards da mesma linha terminarem alinhados. O respiro acima vem do
     margin-bottom de .tcls-card__foot, ja que aqui `auto` ocupa o lugar. */
  margin-top: auto;
  padding: 11px;
  border: none;
  border-radius: 13px;
  background: linear-gradient(
    160deg,
    var(--dk-grape, #a826e1),
    var(--dk-grape-deep, #771ba0)
  );
  color: #fff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.15s ease;
}
.tcls-manage:hover {
  filter: brightness(1.06);
}
.tcls-manage:focus-visible {
  outline: 2px solid var(--grape);
  outline-offset: 2px;
}

/* ---------- Vazio ---------- */
.tcls-empty {
  margin-top: 40px;
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 22px;
}
.tcls-empty__title {
  margin-top: 14px;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--ink);
}
.tcls-empty__text {
  margin-top: 6px;
  max-width: 380px;
  font-size: 0.88rem;
  color: var(--muted);
  line-height: 1.55;
}
.tcls-empty__btn {
  margin-top: 18px;
  padding: 9px 20px;
  border-radius: 12px;
  border: 1px solid var(--grape);
  background: transparent;
  color: var(--grape);
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
}
.tcls-empty__btn:hover {
  background: var(--grape-soft);
}

/* ---------- Responsivo ---------- */
@media (max-width: 720px) {
  .tcls-head {
    flex-direction: column;
  }
  .tcls-tallies {
    gap: 20px;
  }
  .tcls-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  .tcls-search {
    max-width: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tcls-card {
    animation: none;
  }
  .tcls-card:hover {
    transform: none;
  }
}
</style>
